import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

// ─── Types ────────────────────────────────────────────────────────────────────

interface Tournament {
    CLAVE: number;
    NOMBRE: string;
    FECHAINICIO: string;
    FECHATERMINO: string;
    BOWLER_COUNT: number;
}

interface Bowler {
    CLAVE: number;
    FICHANAL: number;
    NOMBRECOMPLETO: string;
    NOMBRE: string;
    APELLIDOP: string;
    DIVISION_CLAVE: number | null;
    DIVISION_NOMBRE: string | null;
}

interface SquadEntry {
    CLAVE: number;
    TURNO: number;
    DIASEMANA_I: string;
    HORARIO_I: string;
    DIFERENCIA: number;
    WLINSCRITOS: number;
    CVEEVENTO: number;
    EVENTNAME: string;
    JUGADO: number;
    MISTERY: number;
    PROGRESSIVE: number;
    LINEAS: number | null;
    MESA: string | null;
    LETRA: string | null;
    PAGADO: number;
    SUSPENDIDO: number;
    HCP: number | null;
}

interface AvailableSquad {
    CLAVE: number;
    TURNO: number;
    DIASEMANA_I: string;
    HORARIO_I: string;
    DIFERENCIA: number;
    WLINSCRITOS: number;
    CVEEVENTO: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AdminMoveSquads: React.FC = () => {
    const navigate = useNavigate();

    // Auth guard
    useEffect(() => {
        if (!localStorage.getItem('loggedInAdmin')) navigate('/admin');
    }, [navigate]);

    // ── Tournament selection ───────────────────────────────────────────
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [tournamentsLoading, setTournamentsLoading] = useState(true);
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);

    // ── Bowler list ────────────────────────────────────────────────────
    const [bowlers, setBowlers] = useState<Bowler[]>([]);
    const [bowlersTotal, setBowlersTotal] = useState(0);
    const [bowlersPage, setBowlersPage] = useState(1);
    const [bowlersLoading, setBowlersLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [activeSearch, setActiveSearch] = useState('');

    // ── Selected bowler & squads ───────────────────────────────────────
    const [selectedBowler, setSelectedBowler] = useState<Bowler | null>(null);
    const [bowlerSquads, setBowlerSquads] = useState<SquadEntry[]>([]);
    const [squadsLoading, setSquadsLoading] = useState(false);
    const [activeEventTab, setActiveEventTab] = useState<number | null>(null);

    // ── Reschedule modal ───────────────────────────────────────────────
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [squadToReschedule, setSquadToReschedule] = useState<SquadEntry | null>(null);
    const [availableSquads, setAvailableSquads] = useState<AvailableSquad[]>([]);
    const [availableLoading, setAvailableLoading] = useState(false);
    const [selectedNewSquad, setSelectedNewSquad] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    // ─── Load tournaments ──────────────────────────────────────────────
    useEffect(() => {
        fetch(`${BACKEND_URL}/api/admin/tournaments-with-counts`)
            .then(r => r.json())
            .then(data => {
                setTournaments(data || []);
                setTournamentsLoading(false);
            })
            .catch(() => setTournamentsLoading(false));
    }, []);

    // ─── Load bowlers whenever tournament / page / search changes ─────
    const loadBowlers = useCallback(async (torneoClave: number, page: number, search: string) => {
        setBowlersLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(PAGE_SIZE),
                ...(search ? { search } : {}),
            });
            const res = await fetch(`${BACKEND_URL}/api/admin/move-squads/bowlers/${torneoClave}?${params}`);
            const data = await res.json();
            setBowlers(data.bowlers || []);
            setBowlersTotal(data.total || 0);
        } catch (err) {
            console.error('Error loading bowlers:', err);
            setBowlers([]);
            setBowlersTotal(0);
        } finally {
            setBowlersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!selectedTournament) return;
        loadBowlers(selectedTournament.CLAVE, bowlersPage, activeSearch);
    }, [selectedTournament, bowlersPage, activeSearch, loadBowlers]);

    // ─── Load squads for selected bowler ──────────────────────────────
    const loadBowlerSquads = async (bowler: Bowler, torneoClave: number) => {
        setSquadsLoading(true);
        setBowlerSquads([]);
        setActiveEventTab(null);
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/move-squads/bowler-squads/${torneoClave}/${bowler.CLAVE}`);
            const data: SquadEntry[] = await res.json();
            setBowlerSquads(data || []);
            // Auto-select the first event tab
            if (data && data.length > 0) {
                setActiveEventTab(data[0].CVEEVENTO);
            }
        } catch (err) {
            console.error('Error loading bowler squads:', err);
        } finally {
            setSquadsLoading(false);
        }
    };

    // ─── Handle bowler selection ───────────────────────────────────────
    const handleSelectBowler = (bowler: Bowler) => {
        setSelectedBowler(bowler);
        if (selectedTournament) {
            loadBowlerSquads(bowler, selectedTournament.CLAVE);
        }
    };

    // ─── Handle tournament selection ───────────────────────────────────
    const handleSelectTournament = (t: Tournament) => {
        setSelectedTournament(t);
        setSelectedBowler(null);
        setBowlerSquads([]);
        setBowlers([]);
        setBowlersPage(1);
        setActiveSearch('');
        setSearchInput('');
    };

    // ─── Search ────────────────────────────────────────────────────────
    const handleSearch = () => {
        setBowlersPage(1);
        setActiveSearch(searchInput.trim());
        setSelectedBowler(null);
        setBowlerSquads([]);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setActiveSearch('');
        setBowlersPage(1);
        setSelectedBowler(null);
        setBowlerSquads([]);
    };

    // ─── Open reschedule modal ─────────────────────────────────────────
    const handleOpenReschedule = async (squad: SquadEntry) => {
        if (!selectedBowler || !selectedTournament) return;
        setSquadToReschedule(squad);
        setSelectedNewSquad(null);
        setSaveError('');
        setShowRescheduleModal(true);
        setAvailableLoading(true);
        setAvailableSquads([]);

        const divisionId = selectedBowler.DIVISION_CLAVE ?? 0;
        try {
            const res = await fetch(
                `${BACKEND_URL}/api/admin/move-squads/available-squads/${selectedTournament.CLAVE}/${squad.CVEEVENTO}/${divisionId}/${squad.CLAVE}`
            );
            const data: AvailableSquad[] = await res.json();
            setAvailableSquads(data || []);
        } catch (err) {
            console.error('Error loading available squads:', err);
            setAvailableSquads([]);
        } finally {
            setAvailableLoading(false);
        }
    };

    // ─── Confirm squad move ────────────────────────────────────────────
    const handleConfirmMove = async () => {
        if (!squadToReschedule || !selectedNewSquad || !selectedBowler || !selectedTournament) return;
        setIsSaving(true);
        setSaveError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/move-squads/move`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: selectedTournament.CLAVE,
                    jugadorClave: selectedBowler.CLAVE,
                    oldSquadId: squadToReschedule.CLAVE,
                    newSquadId: selectedNewSquad,
                }),
            });
            const result = await res.json();
            if (result.success) {
                setShowRescheduleModal(false);
                setSquadToReschedule(null);
                setSelectedNewSquad(null);
                // Reload bowler's squads to reflect the change
                await loadBowlerSquads(selectedBowler, selectedTournament.CLAVE);
            } else {
                setSaveError(result.error || 'Failed to move squad. Please try again.');
            }
        } catch (err) {
            console.error('Error moving squad:', err);
            setSaveError('Failed to move squad. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // ─── Derived data ──────────────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(bowlersTotal / PAGE_SIZE));

    // Group squads by event
    const eventMap: { [eventId: number]: { name: string; squads: SquadEntry[] } } = {};
    for (const squad of bowlerSquads) {
        if (!eventMap[squad.CVEEVENTO]) {
            eventMap[squad.CVEEVENTO] = { name: squad.EVENTNAME, squads: [] };
        }
        eventMap[squad.CVEEVENTO].squads.push(squad);
    }
    const eventIds = Object.keys(eventMap).map(Number);

    const squadStatusLabel = (s: SquadEntry) => {
        if (s.JUGADO === 2) return { label: 'Bowled', color: 'bg-green-100 text-green-700' };
        if (s.SUSPENDIDO === 1) return { label: 'Suspended', color: 'bg-red-100 text-red-700' };
        if (s.PAGADO === 1) return { label: 'Paid', color: 'bg-blue-100 text-blue-700' };
        return { label: 'Registered', color: 'bg-gray-100 text-gray-600' };
    };

    // ─── Render ────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-sm sticky top-0 z-10">
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="text-gray-500 hover:text-indigo-600 transition flex items-center gap-1 text-sm font-medium"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to Dashboard
                        </button>
                        <div className="h-6 w-px bg-gray-200" />
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Move Bowler's Squads</h1>
                            {selectedTournament && (
                                <p className="text-xs text-indigo-600 font-medium">{selectedTournament.NOMBRE}</p>
                            )}
                        </div>
                    </div>
                    {selectedTournament && (
                        <button
                            onClick={() => { setSelectedTournament(null); setSelectedBowler(null); setBowlerSquads([]); }}
                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                            Change tournament
                        </button>
                    )}
                </div>
            </div>

            {/* Tournament Selector */}
            {!selectedTournament && (
                <div className="max-w-4xl mx-auto px-4 py-12">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Select a Tournament</h2>
                    <p className="text-gray-500 mb-8">Choose the tournament you want to manage squad assignments for.</p>
                    {tournamentsLoading ? (
                        <div className="flex justify-center py-16">
                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {tournaments.map(t => (
                                <button
                                    key={t.CLAVE}
                                    onClick={() => handleSelectTournament(t)}
                                    className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1 p-6 text-left border border-transparent hover:border-indigo-200"
                                >
                                    <h3 className="text-lg font-bold text-gray-900 mb-1">{t.NOMBRE}</h3>
                                    <p className="text-sm text-gray-500 mb-3">
                                        {new Date(t.FECHAINICIO).toLocaleDateString()} – {new Date(t.FECHATERMINO).toLocaleDateString()}
                                    </p>
                                    <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20H5a2 2 0 01-2-2V7a2 2 0 012-2h1m0 0V3m0 2h10m0-2v2m1 0h1a2 2 0 012 2v11a2 2 0 01-2 2H5" />
                                        </svg>
                                        {t.BOWLER_COUNT} bowlers
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Main Two-Panel Layout */}
            {selectedTournament && (
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex gap-6" style={{ minHeight: 'calc(100vh - 130px)' }}>

                        {/* ── Left Panel: Bowler List ──────────────────────── */}
                        <div className="w-80 flex-shrink-0 flex flex-col gap-3">
                            {/* Search */}
                            <div className="bg-white rounded-2xl shadow-md p-4">
                                <div className="relative flex gap-2">
                                    <div className="relative flex-1">
                                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <input
                                            type="text"
                                            value={searchInput}
                                            onChange={e => setSearchInput(e.target.value)}
                                            onKeyDown={handleSearchKeyDown}
                                            placeholder="Name or TAT#..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                        />
                                    </div>
                                    <button
                                        onClick={handleSearch}
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition"
                                    >
                                        Go
                                    </button>
                                    {activeSearch && (
                                        <button onClick={handleClearSearch} className="text-gray-400 hover:text-gray-600 px-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-gray-400 mt-2">
                                    {bowlersLoading ? 'Loading...' : `${bowlersTotal} bowlers found`}
                                </p>
                            </div>

                            {/* Bowler list */}
                            <div className="bg-white rounded-2xl shadow-md flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                                {bowlersLoading ? (
                                    <div className="flex justify-center py-10">
                                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent" />
                                    </div>
                                ) : bowlers.length === 0 ? (
                                    <div className="text-center py-10 text-gray-400 text-sm px-4">
                                        {activeSearch ? `No bowlers found for "${activeSearch}"` : 'No bowlers registered in this tournament.'}
                                    </div>
                                ) : (
                                    <ul className="divide-y divide-gray-100">
                                        {bowlers.map(bowler => {
                                            const isSelected = selectedBowler?.CLAVE === bowler.CLAVE;
                                            return (
                                                <li key={bowler.CLAVE}>
                                                    <button
                                                        onClick={() => handleSelectBowler(bowler)}
                                                        className={`w-full text-left px-4 py-3 transition hover:bg-indigo-50 ${isSelected ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''}`}
                                                    >
                                                        <p className={`text-sm font-semibold truncate ${isSelected ? 'text-indigo-700' : 'text-gray-800'}`}>
                                                            {bowler.NOMBRECOMPLETO}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-xs text-gray-400">TAT# {bowler.FICHANAL}</span>
                                                            {bowler.DIVISION_NOMBRE && (
                                                                <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-medium">
                                                                    {bowler.DIVISION_NOMBRE}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="bg-white rounded-2xl shadow-md px-4 py-3 flex items-center justify-between">
                                    <button
                                        onClick={() => setBowlersPage(p => Math.max(1, p - 1))}
                                        disabled={bowlersPage === 1 || bowlersLoading}
                                        className="text-sm font-medium text-indigo-600 disabled:text-gray-300 hover:text-indigo-800 transition flex items-center gap-1"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Prev
                                    </button>
                                    <span className="text-xs text-gray-500">Page {bowlersPage} / {totalPages}</span>
                                    <button
                                        onClick={() => setBowlersPage(p => Math.min(totalPages, p + 1))}
                                        disabled={bowlersPage === totalPages || bowlersLoading}
                                        className="text-sm font-medium text-indigo-600 disabled:text-gray-300 hover:text-indigo-800 transition flex items-center gap-1"
                                    >
                                        Next
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* ── Right Panel: Squads ─────────────────────────── */}
                        <div className="flex-1 min-w-0">
                            {!selectedBowler ? (
                                <div className="bg-white rounded-2xl shadow-md flex items-center justify-center h-64">
                                    <div className="text-center text-gray-400">
                                        <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <p className="text-sm font-medium">Select a bowler on the left</p>
                                        <p className="text-xs mt-1">Their squad assignments will appear here</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-4">
                                    {/* Bowler header card */}
                                    <div className="bg-white rounded-2xl shadow-md px-6 py-4 flex items-center gap-4">
                                        <div className="bg-indigo-100 rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                                            <span className="text-indigo-700 font-bold text-lg">
                                                {selectedBowler.NOMBRE?.charAt(0)}{selectedBowler.APELLIDOP?.charAt(0)}
                                            </span>
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-900">{selectedBowler.NOMBRECOMPLETO}</h2>
                                            <div className="flex items-center gap-3 mt-0.5">
                                                <span className="text-sm text-gray-500">TAT# {selectedBowler.FICHANAL}</span>
                                                {selectedBowler.DIVISION_NOMBRE && (
                                                    <span className="text-sm bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                                                        {selectedBowler.DIVISION_NOMBRE}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {squadsLoading ? (
                                        <div className="bg-white rounded-2xl shadow-md flex justify-center py-12">
                                            <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent" />
                                        </div>
                                    ) : bowlerSquads.length === 0 ? (
                                        <div className="bg-white rounded-2xl shadow-md flex items-center justify-center py-12">
                                            <p className="text-gray-400 text-sm">This bowler has no squad assignments in this tournament.</p>
                                        </div>
                                    ) : (
                                        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                                            {/* Event Tabs */}
                                            <div className="border-b border-gray-100 overflow-x-auto">
                                                <div className="flex">
                                                    {eventIds.map(evId => (
                                                        <button
                                                            key={evId}
                                                            onClick={() => setActiveEventTab(evId)}
                                                            className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                                                                activeEventTab === evId
                                                                    ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
                                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            {eventMap[evId].name}
                                                            <span className="ml-1.5 bg-gray-100 text-gray-600 text-xs rounded-full px-1.5 py-0.5">
                                                                {eventMap[evId].squads.length}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Squads for active tab */}
                                            {activeEventTab !== null && eventMap[activeEventTab] && (
                                                <div className="p-5">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {eventMap[activeEventTab].squads.map(squad => {
                                                            const status = squadStatusLabel(squad);
                                                            const alreadyBowled = squad.JUGADO === 2;
                                                            return (
                                                                <div key={squad.CLAVE} className="border border-gray-100 rounded-xl p-4 hover:border-indigo-200 transition">
                                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                                        <div>
                                                                            <p className="text-sm font-bold text-gray-800">Squad {squad.TURNO}</p>
                                                                            <p className="text-xs text-gray-500 mt-0.5">{squad.DIASEMANA_I}</p>
                                                                        </div>
                                                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status.color}`}>
                                                                            {status.label}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-sm text-gray-700 font-medium mb-3">
                                                                        🕐 {squad.HORARIO_I}
                                                                    </p>
                                                                    <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                                                                        <span>{squad.WLINSCRITOS} registered</span>
                                                                        <span className={squad.DIFERENCIA > 0 ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
                                                                            {squad.DIFERENCIA > 0 ? `${squad.DIFERENCIA} spots left` : 'Full'}
                                                                        </span>
                                                                    </div>
                                                                    {alreadyBowled ? (
                                                                        <div className="text-xs text-gray-400 text-center bg-gray-50 rounded-lg py-2">
                                                                            Already bowled — cannot reschedule
                                                                        </div>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => handleOpenReschedule(squad)}
                                                                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 rounded-lg transition flex items-center justify-center gap-1.5"
                                                                        >
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                                                            </svg>
                                                                            Reschedule
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Reschedule Modal ─────────────────────────────────────── */}
            {showRescheduleModal && squadToReschedule && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
                        {/* Modal Header */}
                        <div className="bg-indigo-600 px-6 py-5 text-white">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-lg font-bold">Reschedule Squad</h3>
                                    <p className="text-indigo-200 text-sm mt-0.5">
                                        {selectedBowler?.NOMBRECOMPLETO} — {squadToReschedule.EVENTNAME}
                                    </p>
                                </div>
                                <button
                                    onClick={() => { setShowRescheduleModal(false); setSquadToReschedule(null); setSaveError(''); }}
                                    className="text-indigo-200 hover:text-white transition mt-0.5"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Current Squad */}
                        <div className="px-6 pt-5 pb-3">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Current Squad</p>
                            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between">
                                <div>
                                    <p className="font-bold text-gray-800">Squad {squadToReschedule.TURNO}</p>
                                    <p className="text-sm text-gray-500">{squadToReschedule.DIASEMANA_I} · {squadToReschedule.HORARIO_I}</p>
                                </div>
                                <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </div>
                        </div>

                        {/* Available Squads */}
                        <div className="px-6 pb-2">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Select New Squad</p>
                            {availableLoading ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-7 w-7 border-4 border-indigo-500 border-t-transparent" />
                                </div>
                            ) : availableSquads.length === 0 ? (
                                <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">
                                    No other available squads for this event and division.
                                </div>
                            ) : (
                                <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                                    {availableSquads.map(sq => {
                                        const isSelected = selectedNewSquad === sq.CLAVE;
                                        const isFull = sq.DIFERENCIA <= 0;
                                        const alreadyRegistered = bowlerSquads.some(bs => bs.CLAVE === sq.CLAVE);
                                        const isDisabled = isFull || alreadyRegistered;
                                        return (
                                            <button
                                                key={sq.CLAVE}
                                                onClick={() => !isDisabled && setSelectedNewSquad(sq.CLAVE)}
                                                disabled={isDisabled}
                                                className={`w-full text-left rounded-xl px-4 py-3 border-2 transition ${
                                                    isDisabled
                                                        ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                                                        : isSelected
                                                        ? 'border-indigo-500 bg-indigo-50'
                                                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className={`font-bold text-sm ${isSelected ? 'text-indigo-700' : 'text-gray-800'}`}>
                                                            Squad {sq.TURNO}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-0.5">{sq.DIASEMANA_I} · {sq.HORARIO_I}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        {alreadyRegistered ? (
                                                            <span className="text-xs text-amber-600 font-semibold">Already registered</span>
                                                        ) : isFull ? (
                                                            <span className="text-xs text-red-500 font-semibold">Full</span>
                                                        ) : (
                                                            <span className="text-xs text-green-600 font-semibold">{sq.DIFERENCIA} spots</span>
                                                        )}
                                                        {isSelected && (
                                                            <div className="mt-1 flex justify-end">
                                                                <svg className="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                </svg>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Error */}
                        {saveError && (
                            <div className="mx-6 mb-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700">
                                {saveError}
                            </div>
                        )}

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
                            <button
                                onClick={() => { setShowRescheduleModal(false); setSquadToReschedule(null); setSaveError(''); }}
                                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmMove}
                                disabled={!selectedNewSquad || isSaving}
                                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2"
                            >
                                {isSaving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Confirm Move
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMoveSquads;
